import kaboom from "kaboom"

// Initialize Kaboom
const k = kaboom({
  background: [26, 26, 46], // Dark blue-ish
  width: window.innerWidth,
  height: window.innerHeight,
  stretch: true,
  letterbox: false,
  crisp: true,
})

// Game constants
const GRAVITY = 500
const JUMP_FORCE = 280
const OBSTACLE_SPEED = 180
const OBSTACLE_SPAWN_INTERVAL = 3.5
const GAP_SIZE = 300
const HITBOX_SCALE = 0.5 // Smaller = more forgiving

// Load assets
k.loadSprite("franklin", "/franklin-sprite.png")
k.loadSprite("skyline", "/skyline.jpg")

// Main game scene
k.scene("game", () => {
  // Track state
  let distance = 0
  let gameOver = false
  let gameStarted = false

  // Scrolling background - scale to fill height, position at bottom
  const bgScale = k.height() / 600
  const bgWidth = 1920 * bgScale // Actual rendered width of skyline

  const bg1 = k.add([
    k.sprite("skyline"),
    k.pos(0, k.height()),
    k.anchor("botleft"),
    k.scale(bgScale),
    k.opacity(0.6),
    k.z(-100),
    "background",
  ])
  const bg2 = k.add([
    k.sprite("skyline"),
    k.pos(bgWidth, k.height()),
    k.anchor("botleft"),
    k.scale(bgScale),
    k.opacity(0.6),
    k.z(-100),
    "background",
  ])

  void bg1
  void bg2

  // Scroll background - seamless loop
  k.onUpdate("background", (bg) => {
    if (gameOver || !gameStarted) return
    bg.pos.x -= OBSTACLE_SPEED * 0.3 * k.dt()
    // When fully off left edge, wrap to the right
    if (bg.pos.x <= -bgWidth) {
      bg.pos.x += bgWidth * 2
    }
  })

  // Ground (invisible, just for collision)
  k.add([
    k.rect(k.width(), 10),
    k.pos(0, k.height() - 10),
    k.area(),
    k.body({ isStatic: true }),
    k.opacity(0),
    "ground",
  ])

  // Ceiling (invisible)
  k.add([
    k.rect(k.width(), 10),
    k.pos(0, 0),
    k.area(),
    k.body({ isStatic: true }),
    k.opacity(0),
    "ceiling",
  ])

  // Player (Ben Franklin!)
  const player = k.add([
    k.sprite("franklin"),
    k.pos(k.width() * 0.2, k.height() * 0.4),
    k.area({ scale: 0.7 }),
    k.body(),
    k.rotate(0),
    k.anchor("center"),
    "player",
  ])

  // Start with no gravity until countdown finishes
  k.setGravity(0)

  // Jump function
  const jump = () => {
    if (gameOver || !gameStarted) return
    player.jump(JUMP_FORCE)
    k.tween(player.angle, -10, 0.2, (v) => player.angle = v)
  }

  // Input handlers
  k.onKeyPress("space", jump)
  k.onClick(jump)
  k.onTouchStart(jump)

  // Tilt down when falling (gentle)
  k.onUpdate(() => {
    if (gameOver || !gameStarted) return
    if (player.vel.y > 0) {
      player.angle = Math.min(player.angle + 0.5, 25)
    }
  })

  // Distance counter
  k.onUpdate(() => {
    if (gameOver || !gameStarted) return
    distance += k.dt() * 100
  })

  // Distance display
  const distanceText = k.add([
    k.text("0m", { size: 32 }),
    k.pos(24, 24),
    k.z(100),
    k.fixed(),
  ])

  k.onUpdate(() => {
    distanceText.text = `${Math.floor(distance)}m`
  })

  // Spawn obstacles (newspaper emoji pairs)
  const spawnObstacle = () => {
    if (gameOver || !gameStarted) return

    const gapY = k.rand(k.height() * 0.2, k.height() * 0.8)

    // Store x position to ensure consistency (rounded to int)
    const startX = Math.round(k.width() + 24)

    // Track spawn time for consistent movement
    const spawnTime = k.time()

    // Helper to create one obstacle
    const createObstacle = (x: number, y: number) => {
      const py = Math.round(y)

      k.add([
        k.text("📰", { size: 48 }),
        k.pos(x, py),
        k.area({ scale: HITBOX_SCALE }),
        k.anchor("center"),
        "obstacle",
        { spawnX: x, spawnTime: spawnTime },
      ])
    }

    // Top obstacles (start at 30 to avoid edge rendering issues)
    const topHeight = Math.round(gapY - GAP_SIZE / 2)
    for (let y = 30; y < topHeight; y += 60) {
      createObstacle(startX, y)
    }

    // Bottom obstacles
    const bottomStart = Math.round(gapY + GAP_SIZE / 2)
    for (let y = bottomStart; y < k.height() - 10; y += 60) {
      createObstacle(startX, y)
    }
  }

  // Obstacle spawning loop (starts after countdown)
  let obstacleLoop: ReturnType<typeof k.loop> | null = null

  // Move obstacles - calculate position from spawn time to avoid drift
  k.onUpdate("obstacle", (obstacle) => {
    if (gameOver) return
    const elapsed = k.time() - obstacle.spawnTime
    obstacle.pos.x = Math.round(obstacle.spawnX - (elapsed * OBSTACLE_SPEED))
    if (obstacle.pos.x < -50) {
      k.destroy(obstacle)
    }
  })


  // Collision handlers
  player.onCollide("obstacle", () => endGame())
  player.onCollide("ground", () => endGame())
  player.onCollide("ceiling", () => endGame())

  // Countdown
  const countdownText = k.add([
    k.text("3", { size: 96 }),
    k.pos(k.center()),
    k.anchor("center"),
    k.z(200),
    k.fixed(),
  ])

  k.wait(0.8, () => {
    countdownText.text = "2"
  })
  k.wait(1.6, () => {
    countdownText.text = "1"
  })
  k.wait(2.4, () => {
    countdownText.text = "FLY!"
  })
  k.wait(3.0, () => {
    k.destroy(countdownText)
    gameStarted = true
    k.setGravity(GRAVITY)
    obstacleLoop = k.loop(OBSTACLE_SPAWN_INTERVAL, spawnObstacle)
    void obstacleLoop
  })

  // End game function
  const endGame = () => {
    if (gameOver) return
    gameOver = true

    // Flash effect
    k.add([
      k.rect(k.width(), k.height()),
      k.pos(0, 0),
      k.color(255, 255, 255),
      k.opacity(0.8),
      k.z(50),
      k.lifespan(0.1),
    ])

    // Shake
    k.shake(10)

    // Show final score
    k.add([
      k.text(`${Math.floor(distance)}m`, { size: 64 }),
      k.pos(k.center()),
      k.anchor("center"),
      k.z(100),
      k.fixed(),
    ])

    k.add([
      k.text("tap to fly again", { size: 24 }),
      k.pos(k.center().x, k.center().y + 50),
      k.anchor("center"),
      k.z(100),
      k.fixed(),
      k.opacity(0.7),
    ])

    // Restart after brief delay
    k.wait(0.8, () => {
      k.onClick(() => k.go("game"))
      k.onKeyPress("space", () => k.go("game"))
      k.onTouchStart(() => k.go("game"))
    })
  }
})

// Start the game immediately
k.go("game")
